import type { UseFormRegisterReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CatalogItemListItem, CatalogItemTypeValue } from '@kreart/shared';
import { useCatalogItems } from '@/features/catalog/hooks';
import { LIBRARY_FEATURE_ENABLED } from '@/features/library/featureFlags';
import { useLibrary } from '@/features/library/hooks';

interface Props {
  type: CatalogItemTypeValue;
  registration: UseFormRegisterReturn;
  placeholder: string;
  errorMessage?: string;
  label: string;
}

/**
 * Native select for picking a catalog item, augmented with the user's Library
 * (per-user pinned catalog items) as a top "Library" optgroup. Library entries
 * are de-duplicated against the full catalog list so a pinned item shows up
 * once. Falls back gracefully when the actor lacks Library access.
 */
export function CatalogItemPicker({ type, registration, placeholder, errorMessage, label }: Props) {
  const { t } = useTranslation();
  const catalogQuery = useCatalogItems({
    page: 1,
    pageSize: 100,
    type,
    includeDeleted: false,
  });
  const libraryQuery = useLibrary({ enabled: LIBRARY_FEATURE_ENABLED });

  const libraryItems: CatalogItemListItem[] = LIBRARY_FEATURE_ENABLED
    ? (libraryQuery.data ?? [])
        .map((row) => row.catalogItem)
        .filter(
          (ci): ci is CatalogItemListItem =>
            Boolean(ci) && ci!.type === type && ci!.deletedAt === null,
        )
    : [];
  const libraryIds = new Set(libraryItems.map((ci) => ci.id));
  const catalogItems = (catalogQuery.data?.items ?? []).filter((ci) => !libraryIds.has(ci.id));

  const renderOption = (ci: CatalogItemListItem) => (
    <option key={ci.id} value={ci.id}>
      {ci.name}
      {ci.sku ? ` — ${ci.sku}` : ''}
    </option>
  );

  return (
    <div>
      <label className="block text-sm font-medium" htmlFor={registration.name}>
        {label}
      </label>
      <select
        id={registration.name}
        className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
        aria-invalid={errorMessage ? true : undefined}
        {...registration}
      >
        <option value="">{placeholder}</option>
        {libraryItems.length > 0 && (
          <optgroup label={t('projectItems.actions.libraryGroup') ?? 'Library'}>
            {libraryItems.map(renderOption)}
          </optgroup>
        )}
        {catalogItems.length > 0 && (
          <optgroup label={t('projectItems.actions.catalogGroup') ?? 'Catalog'}>
            {catalogItems.map(renderOption)}
          </optgroup>
        )}
      </select>
      {errorMessage && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {errorMessage}
        </p>
      )}
    </div>
  );
}
